<template>
    <div class="button-editor">
        <h4>Buttons • {{ buttonLabel }}</h4>
        <p class="description">{{ buttonDescription }}</p>

        <div class="buttons-list">
            <div v-for="(button, index) of buttons" :key="index" class="button-item-wrapper">
                <div v-if="templateType === 'LIMITED_TIME_OFFER'" class="button-index-badge">
                    <span>{{ index }}</span>
                </div>
                <div class="button-item">
                    <div style="display: flex; align-items: center; gap: 8px; width: 180px;">
                        <a-select 
                            v-model="button.type" 
                            style="flex: 1;"
                            :disabled="isButtonTypeDisabled || isCopyCodeButton(button) || isRequiredUrlButton(button)"
                        >
                            <a-select-option 
                                v-for="btnType in availableButtonTypes" 
                                :key="btnType.value" 
                                :value="btnType.value"
                            >
                                {{ btnType.label }}
                            </a-select-option>
                        </a-select>
                        <a-tooltip 
                            v-if="templateType === 'LIMITED_TIME_OFFER' && button.type === 'URL' && isLastUrlButton(button)"
                            title="URL button is required for Limited Time Offer"
                        >
                            <a-tag color="red" style="margin: 0; font-size: 10px; padding: 0 4px;">REQ</a-tag>
                        </a-tooltip>
                    </div>
                    
                    <!-- COPY_CODE button için readonly text (COUPON_CODE template'de) -->
                    <div v-if="isCopyCodeButton(button)" style="flex: 1; display: flex; align-items: center; gap: 8px;">
                        <a-tooltip title="Copy code button text cannot be customized in Coupon Code templates">
                            <a-input 
                                value="Copy code"
                                placeholder="Copy code"
                                style="flex: 1; background-color: #f5f5f5; cursor: not-allowed;" 
                                :maxLength="25"
                                disabled
                                readonly
                            />
                        </a-tooltip>
                        <a-tag color="blue" style="margin: 0; font-size: 11px;">Fixed</a-tag>
                    </div>
                    
                    <!-- Normal button text input -->
                    <a-input 
                        v-else
                        v-model="button.text" 
                        :placeholder="getButtonPlaceholder(button.type)" 
                        style="flex: 1" 
                        :maxLength="25"
                    />
                    <a-input
                        v-if="button.type === 'URL'"
                        v-model="button.url"
                        :placeholder="'https://example.com/' + '{{' + '1' + '}}'"
                        style="flex: 1"
                    />
                    <a-input
                        v-if="button.type === 'PHONE_NUMBER'"
                        v-model="button.phone_number"
                        placeholder="+1234567890"
                        style="flex: 1"
                    />
                    <a-input
                        v-if="button.type === 'COPY_CODE'"
                        v-model="button.example"
                        placeholder="SUMMER20"
                        style="flex: 1"
                    />
                    <a-button 
                        type="link" 
                        @click="removeButton(index)"
                        :disabled="isButtonRemoveDisabled(button)"
                        :style="{ visibility: isButtonRemoveDisabled(button) ? 'hidden' : 'visible' }"
                    >
                        <a-icon type="delete" />
                    </a-button>
                </div>
                <!-- URL Button Example for Dynamic URLs -->
                <div v-if="button.type === 'URL' && hasDynamicUrl(button.url)" class="url-example-section">
                    <div class="url-example-info">
                        <a-icon type="info-circle" />
                        <span>{{ $t("whatsapp_business_api.dynamicUrlSampleNote") }}</span>
                    </div>
                    <div class="url-example-input">
                        <span class="variable-tag" v-text="'{{1}}'"></span>
                        <a-input
                            v-model="button.urlExample"
                            placeholder="example_value"
                            style="flex: 1"
                        />
                    </div>
                </div>
            </div>
        </div>
        <!-- Button Add Actions - İki kategoriye ayrılmış -->
        <div v-if="canAddButton" class="add-button-actions">
            <!-- Quick Reply Button -->
            <a-dropdown :trigger="['click']" v-if="quickReplyButtons.length > 0">
                <a-button> <a-icon type="message" /> Add Quick Reply Button </a-button>
                <a-menu slot="overlay">
                    <a-menu-item 
                        v-for="btnType in quickReplyButtons" 
                        :key="btnType.value" 
                        @click="addButton(btnType.value)"
                    >
                        <a-icon :type="btnType.icon" /> {{ btnType.label }}
                    </a-menu-item>
                </a-menu>
            </a-dropdown>

            <!-- CTA Button -->
            <a-dropdown :trigger="['click']" v-if="ctaButtons.length > 0">
                <a-button type="primary"> <a-icon type="thunderbolt" /> Add CTA Button </a-button>
                <a-menu slot="overlay">
                    <a-menu-item 
                        v-for="btnType in ctaButtons" 
                        :key="btnType.value" 
                        @click="addButton(btnType.value)"
                    >
                        <a-icon :type="btnType.icon" /> {{ btnType.label }}
                    </a-menu-item>
                </a-menu>
            </a-dropdown>
        </div>
    </div>
</template>

<script>
import Vue from "vue";

export default Vue.extend({
    name: "ButtonEditor",
    props: {
        buttons: { type: Array, required: true },
        availableButtonTypes: { type: Array, default: () => [] },
        templateType: { type: String, default: "DEFAULT" },
        category: { type: String, default: "MARKETING" },
    },
    computed: {
        isButtonTypeDisabled() {
            return this.templateType === 'CATALOGUE' || 
                   this.templateType === 'CALL_PERMISSION' || 
                   this.category === 'AUTHENTICATION';
        },
        isRemoveDisabled() {
            // CATALOGUE ve AUTHENTICATION'da silinemez
            if (this.templateType === 'CATALOGUE' || this.category === 'AUTHENTICATION') {
                return true;
            }
            // LIMITED_TIME_OFFER'da minimum 1 buton zorunlu
            if (this.templateType === 'LIMITED_TIME_OFFER' && this.buttons.length === 1) {
                return true;
            }
            return false;
        },
        canAddButton() {
            // CATALOGUE, AUTHENTICATION ve CALL_PERMISSION'da button eklenemez
            if (this.templateType === 'CATALOGUE' || 
                this.templateType === 'CALL_PERMISSION' || 
                this.category === 'AUTHENTICATION') {
                return false;
            }
            return true;
        },
        quickReplyButtons() {
            // Quick Reply kategorisi
            return this.availableButtonTypes.filter(btn => btn.category === 'quick_reply');
        },
        ctaButtons() {
            // CTA (Call-to-Action) kategorisi
            return this.availableButtonTypes.filter(btn => btn.category === 'cta');
        },
        buttonLabel() {
            if (this.templateType === 'CATALOGUE' || 
                this.templateType === 'CALL_PERMISSION' || 
                this.category === 'AUTHENTICATION') {
                return 'Auto-added';
            }
            if (this.templateType === 'COUPON_CODE') {
                return 'Required (1 Copy Code + Optional)';
            }
            if (this.templateType === 'LIMITED_TIME_OFFER') {
                return 'Required (1 URL + Optional)';
            }
            return 'Optional';
        },
        buttonDescription() {
            if (this.templateType === 'CATALOGUE') {
                return 'A product button is automatically added. You can customize the button text.';
            }
            if (this.templateType === 'CALL_PERMISSION') {
                return 'A call permission button is automatically added. No additional buttons can be added.';
            }
            if (this.category === 'AUTHENTICATION') {
                return 'An authentication button is automatically added. You can customize the button text.';
            }
            if (this.templateType === 'COUPON_CODE') {
                return 'One Copy Code button is required and its text cannot be customized. You can add additional Quick Reply or CTA buttons up to 10 total.';
            }
            if (this.templateType === 'LIMITED_TIME_OFFER') {
                return 'Limited time offers require at least one URL button (required). You can add additional CTA buttons (Phone, Copy Offer Code) or Quick Reply buttons.';
            }
            return 'Add Quick Reply buttons for quick responses or CTA buttons for actions. Maximum 10 buttons total.';
        },
    },
    methods: {
        isCopyCodeButton(button) {
            // COUPON_CODE template'inde COPY_CODE button mu?
            return this.templateType === 'COUPON_CODE' && button.type === 'COPY_CODE';
        },
        isRequiredUrlButton(button) {
            // LIMITED_TIME_OFFER'da son URL button type değiştirilemez
            if (this.templateType === 'LIMITED_TIME_OFFER' && button.type === 'URL') {
                const urlButtons = this.buttons.filter(btn => btn.type === 'URL');
                return urlButtons.length === 1;
            }
            return false;
        },
        isLastUrlButton(button) {
            // Bu URL button son URL button mu?
            if (button.type === 'URL') {
                const urlButtons = this.buttons.filter(btn => btn.type === 'URL');
                return urlButtons.length === 1;
            }
            return false;
        },
        isCopyCodeButtonTextDisabled(button) {
            // COUPON_CODE template type'ında COPY_CODE button text customize edilemez
            return this.isCopyCodeButton(button);
        },
        isButtonRemoveDisabled(button) {
            // Genel remove disabled kontrolü
            if (this.isRemoveDisabled) return true;
            
            // COUPON_CODE template için COPY_CODE button silinemez
            if (this.isCopyCodeButton(button)) {
                return true;
            }
            
            // LIMITED_TIME_OFFER için son URL button silinemez
            if (this.templateType === 'LIMITED_TIME_OFFER' && button.type === 'URL') {
                const urlButtons = this.buttons.filter(btn => btn.type === 'URL');
                if (urlButtons.length === 1) {
                    return true;
                }
            }
            
            return false;
        },
        getButtonPlaceholder(type) {
            const placeholders = {
                'COPY_CODE': 'Copy code',
                'CATALOG': 'View catalog',
                'MPM': 'View items',
                'SPM': 'View',
                'OTP': 'Autofill',
                'URL': 'Button text',
                'PHONE_NUMBER': 'Button text',
                'QUICK_REPLY': 'Button text',
            };
            return placeholders[type] || 'Button text';
        },
        hasDynamicUrl(url) {
            return url && /\{\{\d+\}\}/.test(url);
        },
        addButton(type) {
            if (this.buttons.length >= 10) {
                this.$message.warning("Maximum 10 buttons allowed");
                return;
            }
            
            // COUPON_CODE template için COPY_CODE button eklenemez (zaten otomatik ekleniyor)
            if (this.templateType === 'COUPON_CODE' && type === 'COPY_CODE') {
                this.$message.warning("Copy Code button is automatically added and cannot be manually added");
                return;
            }
            
            const defaultText = type === "COPY_CODE" ? "Copy code" :
                               type === "CATALOG" ? "View catalog" :
                               type === "MPM" ? "View items" :
                               type === "SPM" ? "View" : 
                               type === "OTP" ? "Autofill" : "";
            
            this.$emit('add-button', {
                type: type,
                text: defaultText,
                url: "",
                phone_number: "",
                example: "",
                urlExample: "",
            });
        },
        removeButton(index) {
            this.$emit('remove-button', index);
        },
    },
});
</script>

<style scoped lang="scss">
.button-editor {
    position: relative;
    
    h4 {
        margin: 0 0 8px 0;
        font-weight: 600;
    }

    .description {
        color: #666;
        font-size: 14px;
        margin-bottom: 20px;
    }

    .buttons-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 16px;

        .button-item-wrapper {
            display: flex;
            flex-direction: column;
            gap: 8px;
            position: relative;

            .button-index-badge {
                position: absolute;
                left: -40px;
                top: 8px;
                width: 28px;
                height: 28px;
                background: #1890ff;
                color: white;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 13px;
                font-weight: 600;
            }

            &:has(.button-index-badge) {
                margin-left: 40px;
            }
        }

        .button-item {
            display: flex;
            gap: 12px;
            align-items: center;
        }

        .url-example-section {
            margin-left: 192px;
            padding: 12px;
            background: #fffbe6;
            border-radius: 6px;
            border: 1px solid #ffe58f;

            .url-example-info {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 8px;
                font-size: 13px;
                color: #8c8c8c;

                .anticon {
                    color: #faad14;
                }
            }

            .url-example-input {
                display: flex;
                align-items: center;
                gap: 8px;

                .variable-tag {
                    background: #1890ff;
                    color: white;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-family: monospace;
                    font-size: 12px;
                    font-weight: 500;
                }
            }
        }
    }

    .add-button-actions {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }
}
</style>
